import { useCallback } from "react";
import { matchPath, useNavigate } from "react-router-dom";
import { clearPinnedSavedSearchIds } from "../../helper/pinnedSavedSearches";
import { clearOnboardingSearch } from "../../helper/onboardingHandoff";
import { logoutUser } from "../../api/authApi";
import { isDevAuthBypassEnabled } from "../../helper/auth";
import { useAuth } from "../AuthProvider";
import { useThemeMode } from "../ThemeProvider";

const exactPattern = (path) => ({ path, end: true });

export const AUTHENTICATED_ROUTES = {
  search: {
    id: "search",
    label: "Search",
    sidebarLabel: "Search",
    path: "/lists/companies/",
    activePatterns: [exactPattern("/lists/companies/:searchKey?")],
  },
  chat: {
    id: "chat",
    label: "Chat",
    sidebarLabel: "Chats",
    path: "/chat",
    activePatterns: [exactPattern("/chat"), exactPattern("/chats")],
  },
  workspaces: {
    id: "workspaces",
    label: "Workspaces",
    sidebarLabel: "Workspaces",
    path: "/workspaces",
    activePatterns: [
      exactPattern("/workspaces"),
      exactPattern("/workspaces/:id"),
    ],
  },
  conferences: {
    id: "conferences",
    label: "Conferences",
    sidebarLabel: "Conferences",
    path: "/conferences",
    activePatterns: [
      exactPattern("/conferences"),
      exactPattern("/conference/:conferenceId"),
    ],
  },
  admin: {
    id: "admin",
    label: "Admin",
    sidebarLabel: "Admin",
    path: "/admin",
    activePatterns: [exactPattern("/admin")],
    adminOnly: true,
  },
};

export const HEADER_NAV_ORDER = [
  "search",
  "chat",
  "workspaces",
  "conferences",
  "admin",
];

export const CHAT_SIDEBAR_NAV_ORDER = [
  "chat",
  "workspaces",
];

export const getVisibleAuthenticatedNavItems = (order, role) => (
  order
    .map((routeId) => AUTHENTICATED_ROUTES[routeId])
    .filter(Boolean)
    .filter((route) => !route.hidden)
    .filter((route) => !route.adminOnly || role === 1)
);

/** V4 renames the Workspaces nav item to "Saved Search" (path/functionality unchanged). */
export const getAuthenticatedNavItemLabel = (route, { sidebar = false } = {}) => {
  if (!route) return "";
  if (route.id === "workspaces") return "Saved Search";
  return sidebar ? route.sidebarLabel : route.label;
};

export const isAuthenticatedNavItemActive = (route, pathname) => {
  if (!route || typeof pathname !== "string") return false;
  return route.activePatterns.some((pattern) => Boolean(matchPath(pattern, pathname)));
};


const normalizedProfileText = (value) => (
  typeof value === "string" ? value.trim() : ""
);

export const useAuthenticatedAccountActions = () => {
  const navigate = useNavigate();
  const {
    role,
    setRole,
    setToken,
    setUserEmail,
    setUserFirstName,
    setUserLastName,
    userEmail,
    userFirstName,
    userLastName,
  } = useAuth();
  const { darkMode, toggleDarkMode } = useThemeMode();

  const firstName = normalizedProfileText(userFirstName);
  const lastName = normalizedProfileText(userLastName);
  const email = normalizedProfileText(userEmail);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const accountName = fullName || firstName || email || "Account";
  const accountEmail = email && email !== accountName ? email : "";
  const accountInitial = accountName.charAt(0).toLocaleUpperCase();

  const openAccount = useCallback(() => {
    navigate("/settings/account");
  }, [navigate]);

  const changePassword = useCallback(() => {
    navigate("/change-password");
  }, [navigate]);

  const logout = useCallback(async () => {
    // Prototype auth has no server session — skip the logout API call.
    if (!isDevAuthBypassEnabled()) {
      await logoutUser();
    }
    clearPinnedSavedSearchIds();
    clearOnboardingSearch();
    setToken(null);
    setRole(null);
    setUserFirstName(null);
    setUserLastName(null);
    setUserEmail(null);
    navigate("/");
  }, [navigate, setRole, setToken, setUserEmail, setUserFirstName, setUserLastName]);


  return {
    accountInitial,
    accountEmail,
    accountName,
    changePassword,
    darkMode,
    isAdmin: role === 1,
    logout,
    openAccount,
    role,
    toggleDarkMode,
  };
};
